import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import ConfigurationController from './configuration.controller';
import SegmentationStep from './elements/SegmentationStep';
import AvailableBalanceStep from './elements/AvailableBalanceStep';
import FeeStructureStep from './elements/FeeStructureStep';
import LimitsStep from './elements/LimitsStep';
import ConfigurationSuccess from './elements/ConfigurationSuccess';
import Page from '../common/page/Page';
import { Button } from '@clockout-dev/design-elements';
import { TAB_SEARCH_PARAM_KEY, tabs } from './configuration.constants';
import { SCOPES } from '../../constants/scopes.constants';
import { getProductTypeOptions } from './utils/product-state-rules';

/**
 * View layer and single React state owner for the Configuration flow.
 *
 * Owns all render state: page-level fields (loading, error,
 * firstTimeSetup, user, notification, tabKey) plus a `tabs` map holding
 * one slice per tab controller. Tab controllers are view-models that read
 * their slice through `this.state` (a getter backed by this component) and
 * mutate it through this component's real `setState` — no `forceUpdate`,
 * no private mutable state on the controllers. Switching steps and
 * persisting the active tab is URL-driven; `tabKey` carries the latest
 * value into React state so navigation triggers a normal re-render.
 */
class Configuration extends Inherit(ConfigurationController, Component) {
    state = {
        firstTimeSetup: true,
        loading: true,
        error: false,
        user: null,
        /** Cached once in initialize(); role is immutable for the session. */
        canModify: false,
        notification: { show: false, type: 'info', message: '', description: '' },
        tabKey: null,
        /** Replaced by the FI's own catalog in loadConfiguration. */
        productTypeOptions: getProductTypeOptions(null),
        tabs: {
            segmentation:     this.segmentation.getInitialState(),
            availableBalance: this.availableBalance.getInitialState(),
            feeStructure:     this.feeStructure.getInitialState(),
            limits:           this.limits.getInitialState(),
            volumeTiers:      this.feeStructure.volumeTiers.getInitialState(),
            memberPromos:     this.feeStructure.memberPromos.getInitialState()
        }
    };

    renderStep(currentStep) {
        const { firstTimeSetup } = this.state;

        switch (currentStep) {
            case 0:
                return (
                    <SegmentationStep
                        {...this.segmentation.getViewProps()}
                        firstTimeSetup={firstTimeSetup}
                    />
                );
            case 1:
                return (
                    <AvailableBalanceStep
                        {...this.availableBalance.getViewProps()}
                        firstTimeSetup={firstTimeSetup}
                    />
                );
            case 2:
                return (
                    <FeeStructureStep
                        {...this.feeStructure.getViewProps()}
                        firstTimeSetup={firstTimeSetup}
                        onStepChange={this.handleStepChange.bind(this)}
                    />
                );
            case 3: {
                const viewProps = this.limits.getViewProps();
                // The Limits tab is the last step in firstTimeSetup — its
                // submit collapses into the orchestrator's `handleSubmitAll`,
                // which composes a single payload from every tab's
                // `getValues()`. Outside firstTimeSetup we keep the per-tab save.
                return (
                    <LimitsStep
                        {...viewProps}
                        firstTimeSetup={firstTimeSetup}
                        onSubmit={firstTimeSetup ? this.handleSubmitAll.bind(this) : viewProps.onSubmit}
                        onStepChange={this.handleStepChange.bind(this)}
                    />
                );
            }
            case 4:
                return <ConfigurationSuccess onContinue={this.handleContinue.bind(this)} user={this.user} />;
            default:
                return null;
        }
    }

    render() {
        const { loading, error } = this.state;
        const currentStep = this.getCurrentStep();
        const { title, description } = this.getPageMetadata(currentStep);

        if (currentStep === 4) {
            return this.renderStep(currentStep);
        }

        const errorComponent = {
            title: 'Error Loading Configuration',
            subTitle: "We couldn't load your configuration. Please try again.",
            content: (
                <Button onClick={() => window.location.reload()}>
                    Retry
                </Button>
            ),
            isError: error
        };

        return (
            <Page
                isLoading={loading}
                title={title}
                description={description}
                error={errorComponent}
                tabs={tabs}
                activeTabKey={String(currentStep)}
                tabsSearchParamKey={TAB_SEARCH_PARAM_KEY}
                onTabChange={this.handleTabChange.bind(this)}
                scope={SCOPES.CONFIGURATIONS_VIEW}
                notificationContextHolder={this.props.notificationContextHolder}
            >
                {this.renderStep(currentStep)}
            </Page>
        );
    }
}

export default withHooks(Configuration);
